import { useCallback, useContext, useEffect, useRef, useState } from "react"

//FTV Components
import {
  kioskApiPostFTVOptionList,
  kioskApiGetForms,
  kioskApiPutFTVOptionUpdate,
} from "../../kioskApi"

//PrimeReact
import { Button } from "primereact/button"
import { Badge } from "primereact/badge"
import { Dropdown } from "primereact/dropdown"
import { FilterMatchMode } from "primereact/api"
import { InputSwitch } from "primereact/inputswitch"
import { InputText } from "primereact/inputtext"

//SOAR Components
import { UserContext } from "../../../../context/userContext"
import { LogError } from "../../../../helpers/logger"

//Styles
import kioskStyles from "../../../../styles/kiosk/KioskAdmin.module.css"
import myStyles from "../../../../styles/kiosk/KioskAdminFTVOptions.module.css"
import FirstTimeVisitorOptionsForm from "./FirstTimeVisitorOptionsForm"
import { LoaderSmall } from "../../../../components/Loaders"
import { Tooltip } from "primereact/tooltip"
import InactiveKioskLabel from "../InactiveKioskLabel"
import CustomTable from "../CustomComponents/CustomTable"
import { classNames } from "primereact/utils"

export default function FirstTimeVisitorOptions({
  kiosk_organization_id,
  organization_id,
}) {
  const userCtx = useContext(UserContext)

  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingForms, setIsLoadingForms] = useState(true)
  const [options, setOptions] = useState([])
  const [kioskForms, setKioskForms] = useState([])
  const [showForm, setShowForm] = useState(false)
  const [editOptionId, setEditOptionId] = useState(undefined)
  const [deleteOption, setDeleteOption] = useState(false)

  const [listFilters, setListFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    name: { value: null, matchMode: FilterMatchMode.EQUALS },
    formName: { value: null, matchMode: FilterMatchMode.EQUALS },
    description: { value: null, matchMode: FilterMatchMode.EQUALS },
    sortField: "name",
    sortOrder: 1,
  })

  const optionsRef = useRef([])

  const loadForms = useCallback(async () => {
    try {
      setIsLoadingForms(true)

      const forms = await kioskApiGetForms(
        userCtx.tokens.getKioskToken(),
        kiosk_organization_id,
      )

      setKioskForms(forms?.forms || [])
      setIsLoadingForms(false)
    } catch (error) {
      LogError("Unable to load KIOSK forms", error)
      userCtx.setNotificationError("Unable to load Kiosk forms")
      setKioskForms(undefined)
      setIsLoadingForms(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kiosk_organization_id])

  useEffect(() => {
    if (kiosk_organization_id) {
      loadForms()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadForms])

  const loadOptions = useCallback(async () => {
    try {
      if (kiosk_organization_id) {
        setIsLoading(true)
        const results = await kioskApiPostFTVOptionList(
          userCtx.tokens.getKioskToken(),
          {
            kioskOrganizationId: kiosk_organization_id,
            activeOnly: false,
          },
        )

        setOptions(results)
        setIsLoading(false)
      } else {
        setOptions([])
        setIsLoading(false)
      }
    } catch (error) {
      LogError("Unable to load FTV option list", error)
      userCtx.setNotificationError("Unable to load First Time Visitor Options")
      setOptions([])
      setIsLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kiosk_organization_id])

  useEffect(() => {
    loadOptions()
  }, [loadOptions])

  useEffect(() => {
    optionsRef.current = options
  }, [options])

  const toggleOptionActiveStatus = async (profile) => {
    try {
      let index = options.indexOf(profile)

      if (index >= 0) {
        let newOptions = [...optionsRef.current]
        newOptions[index].active = !profile.active
        newOptions[index].updating = true
        setOptions(newOptions)

        await kioskApiPutFTVOptionUpdate(
          userCtx.tokens.getKioskToken(),
          newOptions[index],
        )

        const option = newOptions.find((option) => option.id === profile.id)
        if (option) {
          newOptions = [...optionsRef.current]
          index = newOptions.indexOf(option)
          if (index >= 0) {
            newOptions[index].updating = false
            setOptions(newOptions)
          }
        }
      } else {
        userCtx.setNotificationError(`Option not found: ${profile.name}`)
      }
    } catch (error) {
      LogError("Unable to update FTV profile", error)
      userCtx.setNotificationError(
        `Unable to update option active status: ${profile.name}`,
      )
    }
  }

  const StatusBodyTemplate = (rowData) => {
    return (
      <div className={kioskStyles.switchControl}>
        <InputSwitch
          checked={rowData.active}
          disabled={rowData.updating || rowData.formActive === false}
          onChange={(e) => toggleOptionActiveStatus(rowData)}
        />
      </div>
    )
  }

  const ActionBodyTemplate = (rowData) => {
    if (rowData.updating) {
      return <LoaderSmall />
    } else {
      return (
        <div className={kioskStyles.actionButtonColumn}>
          <Tooltip
            target={`#ftv-edit-${rowData.id}`}
            position="bottom"
            className="custom-tooltip"
          >
            <span>Edit</span>
          </Tooltip>
          <Button
            id={`ftv-edit-${rowData.id}`}
            type="button"
            icon="pi pi-pencil"
            onClick={() => {
              setEditOptionId(rowData.id)
              setDeleteOption(false)
              setShowForm(true)
            }}
            className={kioskStyles.roundedButton}
          />
          <Tooltip
            target={`#ftv-delete-${rowData.id}`}
            position="bottom"
            className="custom-tooltip"
          >
            <span>Delete</span>
          </Tooltip>
          <Button
            id={`ftv-delete-${rowData.id}`}
            type="button"
            icon="pi pi-trash"
            onClick={() => {
              setEditOptionId(rowData.id)
              setDeleteOption(true)
              setShowForm(true)
            }}
            className={kioskStyles.actionButtonPurple}
          />
        </div>
      )
    }
  }

  const NameTemplate = (rowData) => {
    return <div className={kioskStyles.tableData}>{rowData.name}</div>
  }
  const FormNameTemplate = (rowData) => {
    return (
      <div
        className={classNames(kioskStyles.tableData, {
          [myStyles.inactiveBadgeContainer]: rowData.formActive === false,
        })}
      >
        {rowData.formName}
        {rowData.formActive === false && (
          <Badge value="Inactive" severity="danger"></Badge>
        )}
      </div>
    )
  }
  const DescriptionTemplate = (rowData) => {
    return <div className={kioskStyles.tableData}>{rowData.description}</div>
  }

  const headers = () => {
    return [
      { value: "name", label: "Name", dataTemplate: NameTemplate },
      { value: "formName", label: "Form", dataTemplate: FormNameTemplate },
      {
        value: "description",
        label: "Description",
        dataTemplate: DescriptionTemplate,
      },
      {
        value: "active",
        label: "Enabled",
        dataTemplate: StatusBodyTemplate,
        className: myStyles.statusColumn,
      },
      {
        value: "",
        label: "",
        dataTemplate: ActionBodyTemplate,
        className: myStyles.actionColumn,
      },
    ]
  }

  return (
    <>
      {showForm ? (
        <FirstTimeVisitorOptionsForm
          optionId={editOptionId}
          deleteMode={deleteOption}
          kioskOrganizationId={kiosk_organization_id}
          organization_id={organization_id}
          onClose={() => {
            setShowForm(false)
          }}
          onSubmit={() => {
            setShowForm(false)
            loadOptions()
          }}
          onNewKioskForm={(newForm) => {
            setKioskForms([...kioskForms, newForm])
          }}
          kioskForms={kioskForms}
        />
      ) : null}
      <div className={myStyles.ListLayout}>
        <div className={`${myStyles.listLayoutAdd}`}>
          <Button
            label="Add Option"
            disabled={isLoading || isLoadingForms || !kiosk_organization_id}
            loading={isLoadingForms && kiosk_organization_id}
            type="button"
            className={`${kioskStyles.button} ${kioskStyles.primaryButton}`}
            onClick={() => {
              setEditOptionId(undefined)
              setDeleteOption(false)
              setShowForm(true)
            }}
          />
        </div>
        {!kiosk_organization_id && organization_id ? (
          <div className={myStyles.listLayoutList}>
            <InactiveKioskLabel />
          </div>
        ) : (
          <>
            <div className={myStyles.listLayoutSearch}>
              <span className={`p-input-icon-right ${myStyles.filterControl}`}>
                <i className="pi pi-search" />
                <InputText
                  value={listFilters.global.value}
                  onChange={(e) => {
                    const newFilters = { ...listFilters }
                    newFilters.global.value = e.target.value

                    setListFilters(newFilters)
                  }}
                  placeholder="Keyword Search"
                  className={myStyles.filterControl}
                />
              </span>
            </div>
            <div className={myStyles.listLayoutSort}>
              <Dropdown
                placeholder="Sort by"
                value={listFilters.sortField}
                options={[
                  { label: "Sort by: Name", value: "name" },
                  { label: "Sort by: Form name", value: "formName" },
                  { label: "Sort by: Description", value: "description" },
                  { label: "Sort by: Enabled", value: "active" },
                ]}
                onChange={(e) => {
                  setListFilters({
                    ...listFilters,
                    sortField: e.value,
                  })
                }}
                disabled={isLoading}
                className={myStyles.sortList}
              />
            </div>

            <div className={myStyles.listLayoutList}>
              <CustomTable
                headers={headers()}
                collection={options || []}
                sortField={listFilters.sortField}
                sortOrder={listFilters.sortOrder}
                onSort={(newSort) => {
                  setListFilters({
                    ...listFilters,
                    sortField: newSort.sortField,
                    sortOrder: newSort.sortOrder,
                  })
                }}
              />
            </div>
          </>
        )}
      </div>
    </>
  )
}
